import { Link } from '@inertiajs/react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import type { WizardStepKey } from '@/types'

// The full customer-facing journey, in order. The labels are what a founder
// reads; the keys are the Rails wizard steps (residency is answered before the
// formation exists, so it leads the rail but is not a WizardStepCompletion).
// "Structure" is the entity-type question (LLC vs C-Corp); "Ownership and
// roles" is the conditional ownership/governance step.
const STEP_ORDER: WizardStepKey[] = [
  'residency',
  'state',
  'entity_type',
  'name',
  'company',
  'people',
  'structure',
  'tax_setup',
  'review',
  'documents',
  'signature',
  'submission',
  'pay',
]

const STEP_LABELS: Record<WizardStepKey, string> = {
  residency: 'Location',
  state: 'State',
  entity_type: 'Structure',
  name: 'Name',
  company: 'Company',
  people: 'People',
  structure: 'Ownership and roles',
  tax_setup: 'EIN',
  review: 'Review',
  documents: 'Documents',
  signature: 'Signature',
  submission: 'Submission',
  pay: 'Pay now',
}

type WizardProgressRailProps = {
  currentStep: WizardStepKey
  formationId?: string
  resumeStep?: WizardStepKey
  editable?: boolean
  variant: 'desktop' | 'mobile'
}

// Completed earlier steps are real edit links until submission. Future steps
// remain noninteractive, so the rail supports correction without becoming a
// way to skip required answers.
export function WizardProgressRail({
  currentStep,
  formationId,
  resumeStep = currentStep,
  editable = true,
  variant,
}: WizardProgressRailProps) {
  const basePath = useBasePath()
  const currentIndex = STEP_ORDER.indexOf(currentStep)
  const resumeIndex = STEP_ORDER.indexOf(resumeStep)
  const currentLabel = STEP_LABELS[currentStep] || currentStep
  const isReviewingEarlierStep = formationId && currentStep !== resumeStep
  const editableSteps = formationId && editable
    ? STEP_ORDER.slice(0, resumeIndex).filter((step) => step !== currentStep && step !== 'pay')
    : []

  if (variant === 'desktop') {
    return (
      <nav aria-label="Formation progress" data-formation-progress-placement="desktop">
        <p className="mb-3 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Company setup
        </p>
        <ol className="space-y-1">
          {STEP_ORDER.map((step, i) => {
            const done = i < resumeIndex || step === resumeStep
            const isCurrent = i === currentIndex
            const canEdit = editable && Boolean(formationId) && i < resumeIndex && step !== 'pay' && !isCurrent
            const label = STEP_LABELS[step] || step
            const rowClasses = cn(
              'flex min-h-9 w-full items-center gap-3 rounded-md px-2 py-1.5 text-sm transition-colors',
              isCurrent && 'bg-muted font-medium text-foreground',
              !isCurrent && done && 'text-foreground',
              !isCurrent && !done && 'text-muted-foreground/60',
              canEdit && 'hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            )

            const content = (
              <>
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex size-5 shrink-0 items-center justify-center rounded-full border text-xs',
                    done ? 'border-foreground bg-foreground text-background' : 'border-border'
                  )}
                >
                  {done && !isCurrent ? <Check className="size-3" /> : i + 1}
                </span>
                <span className="min-w-0 leading-snug">{label}</span>
                {isCurrent && <span className="sr-only"> (current step)</span>}
                {done && !isCurrent && <span className="sr-only"> (completed)</span>}
              </>
            )

            return (
              <li key={step} aria-current={isCurrent ? 'step' : undefined}>
                {canEdit ? (
                  <Link
                    href={`${basePath}/formations/${formationId}/wizard/${step}/edit`}
                    className={rowClasses}
                    aria-label={`Edit ${label}`}
                  >
                    {content}
                  </Link>
                ) : (
                  <div className={rowClasses}>{content}</div>
                )}
              </li>
            )
          })}
        </ol>
        {isReviewingEarlierStep && (
          <Link
            href={`${basePath}/formations/${formationId}`}
            className="mt-4 inline-flex min-h-11 w-full items-center rounded-md px-2 text-sm font-medium text-primary transition-colors hover:bg-muted/50"
          >
            Return to {STEP_LABELS[resumeStep]}
          </Link>
        )}
      </nav>
    )
  }

  return (
    <nav aria-label="Formation progress" data-formation-progress-placement="mobile">
      <div>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-foreground"
            style={{ width: `${((resumeIndex + 1) / STEP_ORDER.length) * 100}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Step {currentIndex + 1} of {STEP_ORDER.length} — {currentLabel}
        </p>
        {editableSteps.length > 0 && (
          <details className="mt-2">
            <summary className="flex min-h-11 cursor-pointer items-center text-sm font-medium text-foreground">
              Edit completed answers
            </summary>
            <div className="flex flex-wrap gap-2 pb-2">
              {editableSteps.map((step) => (
                <Link
                  key={step}
                  href={`${basePath}/formations/${formationId}/wizard/${step}/edit`}
                  className="inline-flex min-h-11 items-center rounded-md border px-3 text-sm font-medium"
                >
                  {STEP_LABELS[step]}
                </Link>
              ))}
            </div>
          </details>
        )}
      </div>
      {isReviewingEarlierStep && (
        <Link
          href={`${basePath}/formations/${formationId}`}
          className="mt-3 inline-flex min-h-11 items-center text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          Return to {STEP_LABELS[resumeStep]}
        </Link>
      )}
    </nav>
  )
}
